import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getEngineeringSession, setEngineeringSession } from "@/lib/rce/session";
import { scoped } from "@/lib/accountScope";

// Global Surface/Subsea operating-mode context.
// • Persisted in localStorage under "rigsentinel_stack_mode"
// • Allowed values: "surface" | "subsea"
// • Defaults to "surface"
// • Exposed via useStackMode() hook
const STORAGE_KEY = "rigsentinel_stack_mode";
const ALLOWED = ["surface", "subsea"];

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return ALLOWED.includes(v) ? v : "surface";
  } catch {
    return "surface";
  }
}

// Surface/Subsea active-context isolation (Support correction — Aug 2026).
// The Engineering Session (lib/rce/session.js) previously had no
// Surface/Subsea dimension: flipping this mode only changed the display
// label while the active Rig/Well/BHA/Drill String/Hole Section/Current
// Operation stayed whatever they were. This additive, per-mode snapshot
// carries the full active-context ID set so each mode keeps its own
// context. No change to the Engineering Session schema, no duplicate
// canonical Rig/Well records — only which snapshot is "active" changes.
const CTX_STORAGE_KEY = "rsc.stackModeContexts.v1";
const CTX_FIELDS = [
  "projectId", "rigId", "wellId", "bhaId", "drillStringId",
  "mudProgramId", "holeSection", "currentOperation",
];

function _readContexts() {
  try {
    const raw = JSON.parse(scoped.getItem(CTX_STORAGE_KEY));
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

function _writeContexts(contexts) {
  try { scoped.setItem(CTX_STORAGE_KEY, JSON.stringify(contexts)); } catch { /* ignore */ }
}

function _blankContext() {
  const snap = {};
  for (const k of CTX_FIELDS) snap[k] = "";
  return snap;
}

function _snapshotActiveSession() {
  const s = getEngineeringSession() || {};
  const snap = {};
  for (const k of CTX_FIELDS) snap[k] = s[k] || "";
  return snap;
}

// Save the outgoing mode's live context, then restore the incoming mode's —
// a full-replace (never a merge), so nothing from the outgoing mode
// survives underneath the incoming mode. A mode with no prior snapshot
// (first-ever switch into it) starts blank rather than inheriting the
// outgoing mode's context. The very first switch a user ever makes
// doubles as a one-time seed: whatever was already active gets captured
// into the outgoing mode's slot before anything is restored, so no
// pre-existing session is lost.
function _switchStackContext(fromMode, toMode) {
  const contexts = _readContexts();
  contexts[fromMode] = _snapshotActiveSession();
  _writeContexts(contexts);
  setEngineeringSession(contexts[toMode] || _blankContext());
}

const StackModeContext = createContext({
  stackMode: "surface",
  setStackMode: () => {},
});

export function StackModeProvider({ children }) {
  const [stackMode, setStackModeState] = useState(() => readStored());

  // Cross-tab sync: react to other tabs flipping the toggle. Mirror the
  // same context restore locally so this tab's Engineering Session matches
  // the mode it now displays (the tab that actually switched already wrote
  // both mode snapshots to localStorage).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && ALLOWED.includes(e.newValue) && e.newValue !== e.oldValue) {
        const contexts = _readContexts();
        setEngineeringSession(contexts[e.newValue] || _blankContext());
        setStackModeState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setStackMode = useCallback((next) => {
    if (!ALLOWED.includes(next) || next === stackMode) return;
    _switchStackContext(stackMode, next);
    setStackModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, [stackMode]);

  return (
    <StackModeContext.Provider value={{ stackMode, setStackMode }}>
      {children}
    </StackModeContext.Provider>
  );
}

export function useStackMode() {
  return useContext(StackModeContext);
}

export const STACK_MODE_STORAGE_KEY = STORAGE_KEY;
